import {
	DefaultButtonCompat as DefaultButton,
	IDetailsList,
	IPanelProps,
	IPanelStyles,
	PanelCompat as Panel,
	PanelType,
	PrimaryButtonCompat as PrimaryButton,
	StackCompat as Stack,
} from 'components/FluentUICompat';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {EntityListColumn, EntityListFilter} from './EntityList';
import {
	isValueArrayField,
	isValueDateField,
	isValueMatrixField,
	isValueObjectField,
	isValuePrimitiveField,
	PRIMITIVE_TYPE,
} from './EntityUtils';
import {FilterItem, FilterItemDate} from './EntityListFilterItem';
import {FilterOption} from 'types';
import {v4 as uuidv4} from 'uuid';
import {useCommand} from 'hooks';

type EntityListFilterPanelProps = {
	columns: EntityListColumn[];
	finalItems: any[];
	appliedFilters: EntityListFilter[];
	setAppliedFilters: (selectedFilters: EntityListFilter[]) => void;
	selectedFilters: EntityListFilter[];
	setSelectedFilters: (selectedFilters: EntityListFilter[]) => void;
	listRef: React.MutableRefObject<IDetailsList | undefined>;
	isPanelOpened: boolean;
	openPanel: () => void;
	dismissPanel: () => void;
};

export const EntityListFilterPanel: React.FC<
	EntityListFilterPanelProps
> = props => {
	const {t} = useTranslation('components/infinitylist');

	const {
		columns,
		finalItems,
		appliedFilters,
		selectedFilters,
		setAppliedFilters,
		setSelectedFilters,
		listRef,
		isPanelOpened,
		openPanel,
		dismissPanel,
	} = props;

	const filterContent = useMemo(
		() =>
			getFilterContent(
				columns,
				finalItems,
				selectedFilters,
				setSelectedFilters,
			),
		[finalItems, selectedFilters],
	);

	const handleApplyFilters = () => {
		setAppliedFilters([...selectedFilters]);
	};

	const handleRemoveFilters = () => {
		setAppliedFilters([]);
		setSelectedFilters([]);
	};

	const onRenderFooterContent = useCallback(
		() => (
			<div>
				<Stack horizontal tokens={{childrenGap: 10}}>
					<PrimaryButton
						text={t('ApplyFilters')}
						onClick={handleApplyFilters}
					/>
					<DefaultButton
						text={t('ClearFilters')}
						onClick={handleRemoveFilters}
					/>
				</Stack>
			</div>
		),
		[selectedFilters],
	);

	const onRenderBody: IPanelProps['onRenderBody'] = props => {
		return (
			<div
				style={{
					flexGrow: 1,
					overflowY: 'auto',
					padding: '10px 20px 10px 20px',
				}}
			>
				{filterContent}
			</div>
		);
	};

	const handleDismissFilterPanel = () => {
		dismissPanel();
	};

	useCommand(
		{
			key: uuidv4(),
			priority: 1,
			iconProps: {
				iconName: 'Filter',
			},
			farCommand: true,
			onClick() {
				listRef.current?.scrollToIndex(0);
				openPanel();
			},
			ariaLabel: 'Filter',
			title: 'Filter',
		},
		[listRef.current, isPanelOpened],
	);

	useCommand(
		{
			key: 'clearFilter',
			farCommand: true,
			hidden: appliedFilters.length === 0,
			iconProps: {
				iconName: 'ClearFilter',
			},
			onClick() {
				listRef.current?.scrollToIndex(0);
				handleRemoveFilters();
			},
		},
		[listRef.current, appliedFilters, selectedFilters],
	);

	return (
		<Panel
			type={PanelType.medium}
			isLightDismiss
			isOpen={isPanelOpened}
			closeButtonAriaLabel='Close'
			onDismiss={handleDismissFilterPanel}
			headerText={t('Filters')}
			onRenderBody={onRenderBody}
			onRenderFooterContent={onRenderFooterContent}
			isFooterAtBottom={true}
		/>
	);
};

export const getFilterContent = (
	columns: EntityListColumn[],
	finalItems: any[],
	selectedFilters: EntityListFilter[],
	setSelectedFilters: (selectedFilters: EntityListFilter[]) => void,
	hideHeader?: boolean,
	defaultExpanded?: boolean,
): React.ReactElement[] =>
	columns
		.filter(x => x.filterable === true)
		.map((col, i) => {
			const {fieldName, getFieldValue, primitiveFilter} = col;

			const getFieldValueFn =
				getFieldValue || ((item: any) => item[fieldName ?? '']);

			const colValues = finalItems
				.map(item => getFieldValueFn(item))
				.filter(item => item !== null && item !== undefined);

			const dataType = col.dataType || PRIMITIVE_TYPE;

			const propsFilterItem = {
				col,
				selectedFilters,
				setSelectedFilters,
				hideHeader: hideHeader ?? false,
				defaultExpanded: defaultExpanded ?? false,
			};

			if (isValueMatrixField(dataType)) {
				return (
					<FilterItem
						key={col.key}
						{...propsFilterItem}
						{...getMatrixFieldOptions(col, colValues, finalItems.length)}
					/>
				);
			}

			if (isValueArrayField(dataType)) {
				return (
					<FilterItem
						key={col.key}
						{...propsFilterItem}
						{...getArrayFieldOptions(col, colValues, finalItems.length)}
					/>
				);
			}

			if (isValuePrimitiveField(dataType)) {
				return (
					<FilterItem
						key={col.key}
						{...propsFilterItem}
						{...getPrimitiveFieldOptions(col, colValues, finalItems.length)}
					/>
				);
			}

			if (isValueObjectField(dataType)) {
				return (
					<FilterItem
						key={col.key}
						{...propsFilterItem}
						{...getObjectFieldOptions(col, colValues)}
					/>
				);
			}

			if (isValueDateField(dataType)) {
				if (primitiveFilter === true) {
					return (
						<FilterItem
							key={col.key}
							{...propsFilterItem}
							{...getPrimitiveFieldOptions(col, colValues, finalItems.length)}
						/>
					);
				}
				return (
					<FilterItemDate
						key={col.key}
						{...propsFilterItem}
						colValues={colValues}
					/>
				);
			}
			return <></>;
		});

// gets the list of  filter options for the fields that are array of objects
const getMatrixFieldOptions = (
	col: EntityListColumn,
	colValues: any[],
	totalItems: number,
) => {
	const {getFilterKey, getFilterLabel, getTranformMatrix, fnFilterOptionsSort} =
		col;

	const getKeyFn = getFilterKey || ((item: any) => item.id);
	const getLabelFn = getFilterLabel || ((item: any) => item.name);
	const getTranformMatrixFn = getTranformMatrix || ((item: any) => item);
	const getFilterFn = (match: any) => (item: any) =>
		getTranformMatrixFn(item[col.fieldName ?? '']).some(
			(val: any) => getKeyFn(val) === match,
		);

	const getBlankFilterFn = (item: any) =>
		item[col.fieldName ?? '']?.length === 0 ||
		item[col.fieldName ?? ''] === null;

	const flatValues = colValues.map(x => getTranformMatrixFn(x)).flat();
	const uniqueValues = [
		...new Map(flatValues.map(item => [getKeyFn(item), item])).values(),
	];

	const sortOptionsFn = fnFilterOptionsSort || sortOptionsFnDefault;

	const sortedValues: FilterOption[] = uniqueValues
		.sort((v1, v2) => sortOptionsFn(getLabelFn(v1), getLabelFn(v2)))
		.map(x => {
			return {name: getLabelFn(x), optionKey: getKeyFn(x)};
		});

	const blanksExists =
		colValues.length !== totalItems ||
		colValues.filter(x => x.length === 0).length > 0;

	return {sortedValues, getFilterFn, getBlankFilterFn, blanksExists};
};

// gets the list of  filter options for the fields that are array of objects
const getArrayFieldOptions = (
	col: EntityListColumn,
	colValues: any[],
	totalItems: number,
) => {
	const {getFilterKey, getFilterLabel, fnFilterOptionsSort} = col;

	const getKeyFn = getFilterKey || ((item: any) => item.id);
	const getLabelFn = getFilterLabel || ((item: any) => item.name);

	const getFilterFn = (match: any) => (item: any) =>
		item[col.fieldName ?? '']?.some((val: any) => getKeyFn(val) === match);

	const getBlankFilterFn = (item: any) =>
		item[col.fieldName ?? '']?.length === 0 ||
		item[col.fieldName ?? ''] === null;

	const flatValues = colValues.flat();

	const uniqueValues = [
		...new Map(flatValues.map(item => [getKeyFn(item), item])).values(),
	];

	const sortOptionsFn = fnFilterOptionsSort || sortOptionsFnDefault;

	const sortedValues: FilterOption[] = uniqueValues
		.sort((v1, v2) => sortOptionsFn(getLabelFn(v1), getLabelFn(v2)))
		.map(x => {
			return {name: getLabelFn(x), optionKey: getKeyFn(x)};
		});

	const blanksExists =
		colValues.length !== totalItems ||
		colValues.filter(x => x.length === 0).length > 0;

	return {sortedValues, getFilterFn, getBlankFilterFn, blanksExists};
};

// gets the list of filter options for the fields that are singular object
const getObjectFieldOptions = (col: EntityListColumn, colValues: any[]) => {
	const {getFilterKey, getFilterLabel, fnFilterOptionsSort} = col;

	const getKeyFn = getFilterKey || ((item: any) => item.id);

	const getLabelFn = getFilterLabel || ((item: any) => item.name);

	const getFilterFn = (match: any) => (item: any) =>
		getKeyFn(item[col.fieldName || '']) === match;

	const getBlankFilterFn = (item: any) => false;

	const uniqueValues = [
		...new Map(colValues.map(item => [getKeyFn(item), item])).values(),
	];

	const sortOptionsFn = fnFilterOptionsSort || sortOptionsFnDefault;

	const sortedValues: FilterOption[] = uniqueValues
		.sort((v1, v2) => sortOptionsFn(getLabelFn(v1), getLabelFn(v2)))
		.map(x => {
			return {name: getLabelFn(x), optionKey: getKeyFn(x)};
		});

	const blanksExists = false;

	return {sortedValues, getFilterFn, getBlankFilterFn, blanksExists};
};

// gets the list of filter options for the fields that are either a string or a number (something that is not an object with field id)
const getPrimitiveFieldOptions = (
	col: EntityListColumn,
	colValues: any[],
	totalItems: number,
) => {
	const {getFilterKey, getFilterLabel, fnFilterOptionsSort} = col;

	const getLabelFn = getFilterLabel || ((val: any) => val);
	const sortOptionsFn = fnFilterOptionsSort || sortOptionsFnDefault;

	const fnOptionValue = getFilterKey || getLabelFn;

	const getFilterFn = (val: any) => getFieldMatchFn(col, val);

	const getBlankFilterFn = (item: any) =>
		item[col.fieldName ?? ''] === '' || item[col.fieldName ?? ''] === null;

	const sortedValues: FilterOption[] = [...new Set(colValues)]
		.sort((v1, v2) => sortOptionsFn(getLabelFn(v1), getLabelFn(v2)))
		.map(x => {
			return {name: getLabelFn(x), optionKey: fnOptionValue(x)};
		});

	const blanksExists =
		colValues.length !== totalItems ||
		sortedValues.find(x => String(x).trim() === '') === null;

	return {sortedValues, getFilterFn, getBlankFilterFn, blanksExists};
};

// default function to create a generic filter operation of matching
export function getFieldMatchFn(col: EntityListColumn, val: any) {
	const {fieldName, getFieldValue} = col;

	if (getFieldValue) {
		return (item: any) => getFieldValue(item) === val;
	}

	return (item: any) => item[fieldName || ''] === val;
}

// default function to sort the strings
const sortOptionsFnDefault = (a: any, b: any) =>
	String(a).localeCompare(String(b));
